import { User } from "@/interfaces/user";
import { api } from "@/services/api";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Grid, Typography } from "@mui/material";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import * as yup from "yup";
import { FutButton } from "@/components/FutButton";
import RoundInput from "@/components/RoundInput";
import {
  PASSWORD_MATCH,
  REQUIRED_FIELD,
  VALID_CPF,
  VALID_EMAIL,
  VALID_PHONE,
} from "@/constants/yupErrors";
import { cpfIsValid } from "@/utils/cpfValidator";
import { phoneRegex } from "@/utils/regex";

interface PerfilFormProps {
  user: User;
  setEditStatus: (value: boolean) => void;
  editStatus: boolean;
}

const personalSchema = yup.object({
  name: yup.string().required(REQUIRED_FIELD),
  email: yup.string().required(REQUIRED_FIELD).trim().email(VALID_EMAIL),
  phone: yup.string().required(REQUIRED_FIELD).matches(phoneRegex, VALID_PHONE),
  cpf: yup
    .string()
    .required(REQUIRED_FIELD)
    .test(
      "is-cpf-valid",
      () => VALID_CPF,
      (value) => cpfIsValid(value)
    ),
  cep: yup.string().required(REQUIRED_FIELD),
  state: yup.string().required(REQUIRED_FIELD),
  city: yup.string().required(REQUIRED_FIELD),
  address: yup.string().required(REQUIRED_FIELD),
  pix_key: yup.string().required(REQUIRED_FIELD),
});

type PersonalFormType = yup.InferType<typeof personalSchema>;

export default function PerfilForm({ user, setEditStatus, editStatus }: PerfilFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<PersonalFormType>({
    defaultValues: {
      name: user?.name || "",
      email: user?.email || "",
      phone: user?.phone || "",
      cpf: user?.cpf || "",
      cep: user?.cep || "",
      state: user?.state || "",
      city: user?.city || "",
      address: user?.address || "",
      pix_key: user?.pix_key || "",
    },
    resolver: yupResolver(personalSchema),
  });

  const onSubmit = async (data: PersonalFormType) => {
    try {
      setIsSubmitting(true);

      // Remove non-numeric characters from CPF
      const formattedCpf = data.cpf.replace(/[^\d]/g, "");

      await api.put(`/user/edit/${user.id}`, {
        ...data,
        cpf: formattedCpf,
      });

      toast.success("Perfil atualizado com sucesso!");
      setEditStatus(false);
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error("Ocorreu um erro ao atualizar o perfil. Tente novamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ padding: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <RoundInput
            inputName="name"
            control={control}
            fullWidth
            size="small"
            placeholder="Nome completo"
            topLabel="Nome completo"
            errorText={errors.name}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <RoundInput
            inputName="email"
            control={control}
            fullWidth
            size="small"
            placeholder="Email"
            topLabel="Email"
            errorText={errors.email}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <RoundInput
            inputName="cpf"
            control={control}
            fullWidth
            size="small"
            placeholder="CPF"
            topLabel="CPF"
            errorText={errors.cpf}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <RoundInput
            inputName="pix_key"
            control={control}
            fullWidth
            size="small"
            placeholder="Chave Pix"
            topLabel="Chave Pix"
            errorText={errors.pix_key}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <RoundInput
            inputName="phone"
            control={control}
            fullWidth
            size="small"
            placeholder="Telefone"
            topLabel="Telefone"
            errorText={errors.phone}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <RoundInput
            inputName="cep"
            control={control}
            fullWidth
            size="small"
            placeholder="CEP"
            topLabel="CEP"
            errorText={errors.cep}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <RoundInput
            inputName="state"
            control={control}
            fullWidth
            size="small"
            placeholder="Estado"
            topLabel="Estado"
            errorText={errors.state}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <RoundInput
            inputName="city"
            control={control}
            fullWidth
            size="small"
            placeholder="Cidade"
            topLabel="Cidade"
            errorText={errors.city}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <RoundInput
            inputName="address"
            control={control}
            fullWidth
            size="small"
            placeholder="Endereço"
            topLabel="Endereço"
            errorText={errors.address}
          />
        </Grid>
      </Grid>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <FutButton variant="text" onClick={() => setEditStatus(false)}>
          Cancelar
        </FutButton>
        <FutButton type="submit" isLoading={isSubmitting}>
          Salvar
        </FutButton>
      </Box>
    </Box>
  );
}